import React, { useCallback, useEffect } from 'react';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import Sentry from 'Integrations/Sentry';
import isDev from 'Helpers/isDev';
import { sendEvent } from './utils';

function getInitialState() {
  return {
    yodleeConfigResource: null,
    yodleeConfigCollection: null,
    eventsCollection: null,
    isInitialized: false,
    initializationError: null,
  };
}

export const COLLECTIONS_RETRIEVED = 'collections-retrieved';
export const INITIALIZATION_SUCCEEDED = 'initialize-succeeded';
export const ERROR_INITIALIZING = 'error-initializing';

const fastlinkTimeoutCount = 160;

function reducer(state, action) {
  switch (action.type) {
    case COLLECTIONS_RETRIEVED: {
      const [yodleeConfigResource] = action.yodleeConfigCollection;
      return {
        ...state,
        yodleeConfigResource,
        yodleeConfigCollection: action.yodleeConfigCollection,
        eventsCollection: action.eventsCollection,
      };
    }
    case INITIALIZATION_SUCCEEDED: {
      return {
        ...state,
        isInitialized: true,
      };
    }
    case ERROR_INITIALIZING: {
      return {
        ...state,
        initializationError: action.initializationError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

let yodleeIsClosed = true;

export default function useYodleeFastLink(resource, elementId, args = {}) {
  const {
    institutionName,
    yodleeConfigLink,
    onSuccess,
    onErrorLoadingYodlee,
    onClose,
    onTimeout,
  } = args;
  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);
  const timerRef = React.useRef(0);
  const shouldTimeout = React.useRef(true);

  const onMount = async () => {
    try {
      let linkToUse = resource.withRelationship(
        fr.relationships.yodleeLink.name,
        (rel) => rel.link('related'),
        () => null,
      );

      if (yodleeConfigLink) {
        linkToUse = yodleeConfigLink;
      }

      const yodleeConfigCollection = await linkToUse.get();
      const [yodleeConfigResource] = yodleeConfigCollection;
      const eventsColl = await yodleeConfigResource.relationship(fr.relationships.events.name)?.link('related').get();

      dispatch({
        type: COLLECTIONS_RETRIEVED,
        yodleeConfigCollection,
        eventsCollection: eventsColl,
      });
    } catch (err) {
      dispatch({ type: ERROR_INITIALIZING, initializationError: err });
      Sentry.createSentryErrorLog(err, err?.status);
    }
  };

  const reset = useCallback(() => {
    timerRef.current = 0;
    yodleeIsClosed = true;
    try {
      window.fastlink.close();
    } catch (err) {
      Sentry.createSentryErrorLog(err);
    }
  }, []);

  useComponentDidMount(onMount, reset);

  const {
    yodleeConfigResource,
    eventsCollection,
    isInitialized,
  } = state;

  const handleClickInitiate = React.useCallback(async () => {
    if (!yodleeIsClosed) {
      return;
    }
    if (!state.yodleeConfigResource) {
      return;
    }
    timerRef.current = 0;


    const token = yodleeConfigResource.attr('token');
    const fastLinkUrl = yodleeConfigResource.attr('url');

    const institutionId = yodleeConfigResource.attr('providerId');
    const providerAccountId = yodleeConfigResource.attr('providerAccountId');

    // Not awaiting because we don't need to block for this
    sendEvent(eventsCollection, token, {
      providerId: institutionId,
      providerName: institutionName,
      status: 'OPENED',
    }, 'OPENED');

    const opts = {
      configName: 'default',
    };

    if (institutionId && institutionId !== '') {
      opts.providerId = parseInt(institutionId, 10);
      opts.flow = yodleeConfigResource.attr('flowType');
    } else if (providerAccountId && providerAccountId !== '') {
      opts.providerAccountId = parseInt(providerAccountId, 10);
      opts.flow = yodleeConfigResource.attr('flowType');
    }
    try {
      window.fastlink.open({
        fastLinkURL: fastLinkUrl,
        forceIframe: true,
        iframeScrolling: 'yes',
        accessToken: `Bearer ${token}`,
        params: opts,
        onSuccess(data) {
          shouldTimeout.current = false;
          timerRef.current = 0;
          sendEvent(eventsCollection, token, data, 'SUCCESS');
          // will be called on success. For list of possible message, refer to onSuccess(data) Method.
          if (typeof onSuccess === 'function') {
            onSuccess(data, yodleeConfigResource, eventsCollection);
          }
        },
        onError(data) {
          shouldTimeout.current = false;
          timerRef.current = 0;
          // Yodlee returns errors that we don't really want to show users an error message for.
          // Let Yodlee present the user the error on their own, and we'll just log the event
          sendEvent(eventsCollection, token, data, 'ERROR');

          // Let's also get an error out in dev though
          if (isDev()) {
            console.error('useYodleeFastLink got an error from the Yodlee iframe', data); // eslint-disable-line no-console
          }
        },
        onClose(data) {
          shouldTimeout.current = false;
          timerRef.current = 0;
          if (data.sites) {
            data.sites.forEach(siteData => {
              sendEvent(eventsCollection, token, siteData, 'EXIT');
            });
          }

          // will be called to close FastLink. For list of possible message, refer to onClose(data) Method.
          if (typeof onClose === 'function') {
            onClose(data, yodleeConfigResource, eventsCollection);
          }
        },
        onEvent(data) {
          // there are times after entering your credentials that fastlink verifies data for an indefinite time.
          // the data we get from the onEvent callback are not helpful to know what step in fastlink the client is.
          // Since this callback gets called constantly through the fastlink process,
          // we are saying that after being called a certain number of times, we should time out unless we get the other callbacks first.
          // Ideally we'd check what's going on with fastlink, and only time out if it's making no progress by checking if the text in #login-refresh-label has changed.
          // However, since it's in an iframe on a different origin, we can't read that, and Yodlee doesn't give us this kind of info in other ways either.

          timerRef.current += 1;
          if (timerRef.current > fastlinkTimeoutCount && shouldTimeout.current) {
            const dataCopy = data;
            dataCopy.providerId = yodleeConfigResource.attr('providerId');
            dataCopy.reason = 'this is a Facet forced timeout on Yodlee';
            sendEvent(eventsCollection, token, dataCopy, 'TIMEOUT');
            reset();
            onTimeout();
          }
        },
      },
      elementId);
      yodleeIsClosed = false; // Even if we get an error later, it's fine, yodlee is not *closed* at this point, we need to run window.fastlink.close if we want to change that
      dispatch({ type: INITIALIZATION_SUCCEEDED });
    } catch (err) {
      dispatch({ type: ERROR_INITIALIZING, initializationError: err });
      Sentry.createSentryErrorLog(err, err?.status);
      onErrorLoadingYodlee(err);
    }
  }, [yodleeConfigResource, eventsCollection, reset, dispatch]);

  // Pretend the user has already clicked the button to initiate so that Yodlee can load before the user clicks the button.
  // Note that we still need a click handler because if the user dismisses Yodlee, we need to close Yodlee, but still allow the user to open it back up again
  useEffect(() => {
    handleClickInitiate();
  }, [handleClickInitiate, reset]);

  return {
    handleClickInitiate,
    isInitialized,
    initializationError: state.initializationError,
  };
}
